import React, { useState } from "react";
import {
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import BottomSheet from "./BottomSheet";
import { Colors } from "@/constants/Colors";
import { FontFamilies } from "@/constants/Typography";
import CommonServices from "@/services/commonServices";
import { showSuccess, showError, showWarning } from "@/utils/toast";

const SUPPORT_OPTIONS = [
  "My payment went through but I can't access paid features.",
  "I was charged incorrectly or multiple times.",
  "I'm having trouble canceling my subscription.",
  "I'd like to report an error.",
  "Other",
] as const;

interface ContactSupportBottomSheetProps {
  isVisible: boolean;
  onClose: () => void;
}

const ContactSupportBottomSheet: React.FC<ContactSupportBottomSheetProps> = ({
  isVisible,
  onClose,
}) => {
  const insets = useSafeAreaInsets();
  const [step, setStep] = useState<1 | 2>(1);
  const [selectedTopic, setSelectedTopic] = useState<string | null>(null);
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const bottomInset = insets.bottom || 60;

  const handleSelectTopic = (topic: string) => {
    setSelectedTopic(topic);
  };

  const handleCancel = () => {
    setStep(1);
    setSelectedTopic(null);
    setMessage("");
    onClose();
  };

  const handleSubmitStep1 = () => {
    if (!selectedTopic) {
      showWarning("Please select a topic");
      return;
    }
    setStep(2);
  };

  const handleSubmitStep2 = async () => {
    if (!selectedTopic) return;

    setIsSubmitting(true);
    try {
      const response = await CommonServices.submitSupportRequest({
        topic: selectedTopic,
        message: message.trim(),
      });

      if (response.success) {
        showSuccess(
          "Your support request has been submitted. We'll get back to you soon!",
          "Request Submitted",
        );
        handleCancel();
      } else {
        showError("Failed to submit support request. Please try again.");
      }
    } catch {
      showError("Failed to submit support request. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStep1 = () => (
    <>
      <Text style={styles.title}>Contact support</Text>
      <View style={styles.optionsContainer}>
        {SUPPORT_OPTIONS.map((option, index) => (
          <TouchableOpacity
            key={index}
            style={[
              styles.optionButton,
              selectedTopic === option && styles.optionButtonSelected,
            ]}
            onPress={() => handleSelectTopic(option)}
          >
            <Text style={styles.optionText}>{option}</Text>
          </TouchableOpacity>
        ))}
      </View>
      <View style={styles.buttonsContainer}>
        <TouchableOpacity style={styles.cancelButton} onPress={handleCancel}>
          <Text style={styles.cancelButtonText}>Cancel</Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[
            styles.submitButton,
            !selectedTopic && styles.submitButtonDisabled,
          ]}
          onPress={handleSubmitStep1}
          disabled={!selectedTopic}
        >
          <Text style={styles.submitButtonText}>Submit</Text>
        </TouchableOpacity>
      </View>
    </>
  );

  const renderStep2 = () => (
    <>
      <Text style={styles.title}>Tell us what happened</Text>
      <View style={styles.textInputContainer}>
        <TextInput
          style={styles.textInput}
          placeholder="Describe your issue..."
          placeholderTextColor="#9CA3AF"
          multiline
          value={message}
          onChangeText={setMessage}
          textAlignVertical="top"
        />
      </View>
      <View style={styles.buttonsContainer}>
        <TouchableOpacity style={styles.cancelButton} onPress={handleCancel}>
          <Text style={styles.cancelButtonText}>Cancel</Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[
            styles.submitButton,
            isSubmitting && styles.submitButtonDisabled,
          ]}
          onPress={handleSubmitStep2}
          disabled={isSubmitting}
        >
          <Text style={styles.submitButtonText}>
            {isSubmitting ? "Submitting..." : "Submit"}
          </Text>
        </TouchableOpacity>
      </View>
    </>
  );

  return (
    <BottomSheet
      isVisible={isVisible}
      onClose={handleCancel}
      height={(step === 1 ? 540 : 320) + bottomInset}
      enableBackdropDismiss
      enableSwipeDown
    >
      <View style={styles.container}>
        {step === 1 ? renderStep1() : renderStep2()}
      </View>
    </BottomSheet>
  );
};

export default ContactSupportBottomSheet;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingHorizontal: 0,
  },
  title: {
    fontSize: 17,
    fontFamily: FontFamilies.Inter.SemiBold,
    textAlign: "center",
    marginBottom: 16,
    color: "#000",
  },
  optionsContainer: {
    gap: 10,
  },
  optionButton: {
    backgroundColor: "#E1EFE4",
    borderRadius: 8,
    paddingHorizontal: 20,
    paddingVertical: 19,
    minHeight: 64,
    justifyContent: "center",
    alignItems: "center",
  },
  optionButtonSelected: {
    backgroundColor: "#C8E6CC",
    borderWidth: 2,
    borderColor: Colors.primary,
  },
  optionText: {
    fontSize: 15,
    fontFamily: FontFamilies.Inter.Medium,
    color: "#161D14",
    textAlign: "center",
    lineHeight: 23,
  },
  textInputContainer: {
    backgroundColor: "#F3F3F3",
    borderRadius: 16,
    padding: 20,
    minHeight: 153,
    marginBottom: 16,
  },
  textInput: {
    flex: 1,
    fontSize: 15,
    fontFamily: FontFamilies.Inter.Regular,
    color: "#161D14",
    minHeight: 100,
  },
  buttonsContainer: {
    flexDirection: "row",
    gap: 10,
    marginTop: 16,
  },
  cancelButton: {
    flex: 1,
    height: 48,
    backgroundColor: "rgba(120, 120, 128, 0.16)",
    borderRadius: 100,
    justifyContent: "center",
    alignItems: "center",
  },
  cancelButtonText: {
    fontSize: 17,
    fontFamily: FontFamilies.Inter.Medium,
    color: "#000",
  },
  submitButton: {
    flex: 1,
    height: 48,
    backgroundColor: Colors.primary,
    borderRadius: 100,
    justifyContent: "center",
    alignItems: "center",
  },
  submitButtonDisabled: {
    opacity: 0.5,
  },
  submitButtonText: {
    fontSize: 17,
    fontFamily: FontFamilies.Inter.Medium,
    color: "#FFF",
  },
});
